import {
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormControl, FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import * as L from 'leaflet';
import { LocationFormMode } from '@ep/shared';
import { LocationCoordinatesFormGroup } from '@app/areas/merchants/services/location-form.service';
import { GeocodeResult, GeocodingService, roundCoordinate } from '@app/resources/services/geocoding.service';

// The contiguous United States, shown until a place is picked.
const US_BOUNDS = L.latLngBounds([24.396308, -124.848974], [49.384358, -66.885444]);
const PLACE_ZOOM = 16;

// Coordinates are rounded before they're stored, so a pin within this margin is already in place.
const SAME_POSITION_MARGIN = 1e-6;

// Leaflet's default icon resolves its image path from leaflet.css, which breaks once the build hashes the
// file names, so point at the copies angular.json puts in assets/leaflet.
const MARKER_ICON = L.icon({
  iconUrl: 'assets/leaflet/marker-icon.png',
  iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
  shadowUrl: 'assets/leaflet/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
});

const SKELETON_ROWS = [
  { LabelWidth: '75%', DescriptionWidth: '95%' },
  { LabelWidth: '60%', DescriptionWidth: '85%' },
  { LabelWidth: '70%', DescriptionWidth: '90%' },
];

@Component({
  selector: 'app-location-map-picker',
  templateUrl: './location-map-picker.component.html',
  styleUrls: ['./location-map-picker.component.scss'],
  standalone: true,
  imports: [ButtonModule, InputTextModule, SkeletonModule, TooltipModule, FormsModule, NgFor, NgIf],
})
export class LocationMapPickerComponent implements OnInit, OnChanges, OnDestroy {
  private readonly geocodingService = inject(GeocodingService);
  private readonly zone = inject(NgZone);

  @Input({ required: true }) coordinates!: LocationCoordinatesFormGroup;
  @Input({ required: true }) address!: FormControl<string | null>;
  @Input({ required: true }) mode!: LocationFormMode;

  // The map is only rendered while editing, so build and tear it down as its container comes and goes.
  @ViewChild('mapContainer') private set mapContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container && !this.map) {
      this.createMap(container.nativeElement);
    } else if (!container && this.map) {
      this.destroyMap();
    }
  }

  protected query = '';
  protected results: GeocodeResult[] | null = null;
  protected pinnedResultId: number | null = null;
  protected isSearching = false;
  protected isLocating = false;
  protected searchFailed = false;
  protected readonly skeletonRows = SKELETON_ROWS;

  private map?: L.Map;
  private marker?: L.Marker;
  private resizeObserver?: ResizeObserver;
  private searchSubscription?: Subscription;
  private subs = new Subscription();

  protected get hasCoordinates(): boolean {
    const { Latitude, Longitude } = this.coordinates.controls;
    return Latitude.value !== null && Longitude.value !== null;
  }

  ngOnInit(): void {
    this.subs.add(this.coordinates.valueChanges.subscribe(() => this.syncMarker()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode']) {
      this.searchSubscription?.unsubscribe();
      this.isSearching = false;
      this.isLocating = false;
      this.results = null;
      this.pinnedResultId = null;
      this.searchFailed = false;
      this.query = this.mode === 'view' ? '' : this.initialQuery();

      // A Location with saved coordinates opens on its pin. Otherwise start from its address and pin the best match.
      if (this.mode !== 'view' && !this.hasCoordinates) {
        this.search(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.destroyMap();
  }

  protected search(pinBestMatch = false): void {
    const query = this.query.trim();
    if (!query || this.isSearching) {
      return;
    }

    this.isSearching = true;
    this.isLocating = pinBestMatch;
    this.searchFailed = false;

    this.searchSubscription = this.geocodingService.searchUsAddress(query).subscribe((results) => {
      this.isSearching = false;
      this.isLocating = false;
      this.searchFailed = results === null;
      this.results = results ?? [];
      this.pinnedResultId = null;

      // The list stays open after pinning, in case the best match isn't the right place.
      const [bestMatch] = this.results;
      if (pinBestMatch && bestMatch && !this.hasCoordinates) {
        this.setCoordinates(bestMatch.Latitude, bestMatch.Longitude);
        this.pinnedResultId = bestMatch.Id;
      }
    });
  }

  protected pick(result: GeocodeResult): void {
    this.setCoordinates(result.Latitude, result.Longitude);

    if (!this.address.value) {
      this.address.setValue(result.Label);
      this.address.markAsDirty();
    }

    this.results = null;
    this.pinnedResultId = null;
  }

  private initialQuery(): string {
    const { Latitude: latitude, Longitude: longitude } = this.coordinates.getRawValue();
    if (latitude !== null && longitude !== null) {
      return `${latitude}, ${longitude}`;
    }

    return this.address.value ?? '';
  }

  private setCoordinates(latitude: number, longitude: number): void {
    this.coordinates.setValue({ Latitude: roundCoordinate(latitude), Longitude: roundCoordinate(longitude) });
    this.coordinates.markAsDirty();
  }

  private createMap(container: HTMLDivElement): void {
    this.zone.runOutsideAngular(() => {
      const map = L.map(container, { scrollWheelZoom: false }).fitBounds(US_BOUNDS);

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      map.on('focus', () => map.scrollWheelZoom.enable());
      map.on('blur', () => map.scrollWheelZoom.disable());

      this.resizeObserver = new ResizeObserver(() => map.invalidateSize());
      this.resizeObserver.observe(container);

      this.map = map;
    });

    this.syncMarker();
  }

  private destroyMap(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.map?.remove();
    this.map = undefined;
    this.marker = undefined;
  }

  private syncMarker(): void {
    const map = this.map;
    if (!map) {
      return;
    }

    const { Latitude: latitude, Longitude: longitude } = this.coordinates.getRawValue();

    this.zone.runOutsideAngular(() => {
      if (latitude === null || longitude === null) {
        if (this.marker) {
          this.marker.remove();
          this.marker = undefined;
          map.fitBounds(US_BOUNDS);
        }
        return;
      }

      const position = L.latLng(latitude, longitude);

      if (!this.marker) {
        this.marker = L.marker(position, { icon: MARKER_ICON, draggable: true }).addTo(map);
        this.marker.on('dragend', (event: L.DragEndEvent) => {
          const { lat, lng } = (event.target as L.Marker).getLatLng();
          this.zone.run(() => this.setCoordinates(lat, lng));
        });
      } else if (this.marker.getLatLng().equals(position, SAME_POSITION_MARGIN)) {
        return;
      } else {
        this.marker.setLatLng(position);
      }

      map.setView(position, PLACE_ZOOM);
    });
  }
}
